import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'

import { explorerApi } from '~/api/client'
import {
  type NexonLwWeeklyBurnSourcesInfo,
  NexonLwWeeklyBurnSourcesRequest,
} from '~/api/repository'

// UI period를 API period로 매핑
const PERIOD_MAP: Record<string, NexonLwWeeklyBurnSourcesRequest.period> = {
  '3M': NexonLwWeeklyBurnSourcesRequest.period.M3,
  '6M': NexonLwWeeklyBurnSourcesRequest.period.M6,
  '1Y': NexonLwWeeklyBurnSourcesRequest.period.Y1,
  MAX: NexonLwWeeklyBurnSourcesRequest.period.ALL,
}

export type WeeklyBurnPeriod = '3M' | '6M' | '1Y' | 'MAX';

export const getWeeklyBurnSources = (period: Ref<WeeklyBurnPeriod>) => {
  return useQuery({
    queryKey: [ 'weeklyBurnSources', period ],
    queryFn: () =>
      explorerApi.lwController
        .getWeeklyBurnSources({
          requestBody: { period: PERIOD_MAP[period.value] },
        })
        .then((res) => weeklyBurnSourcesDto(res)),
  })
}

export type WeeklyBurnSourcesView = {
  weeks: WeeklyBurnBarView[];
  categories: string[];
  categoryColors: Record<string, string>;
};

export type WeeklyBurnBarView = {
  label: string;
  startDate: Date;
  endDate: Date;
  items: WeeklyBurnItemView[];
  total: number;
};

export type WeeklyBurnItemView = {
  name: string;
  burnValue: number;
  count: number;
  color: string;
};

// 기본 색상 팔레트 (새 카테고리 대비)
const DEFAULT_COLORS = [
  '#ffc400',
  '#ff9800',
  '#f44336',
  '#3f51b5',
  '#9c27b0',
  '#6ea8fe',
  '#673ab7',
]


const weeklyBurnSourcesDto = (
  data: NexonLwWeeklyBurnSourcesInfo
): WeeklyBurnSourcesView => {
  // 모든 카테고리 수집
  const categorySet = new Set<string>()
  data.DATA.forEach((bar) => {
    bar.ITEMS.forEach((item) => categorySet.add(item.NM))
  })
  const categories = Array.from(categorySet).sort((a, b) => a.localeCompare(b))

  // 카테고리별 색상 매핑
  const categoryColors: Record<string, string> = {}
  categories.forEach((categoryName, index) => {
    categoryColors[categoryName] = DEFAULT_COLORS[index % DEFAULT_COLORS.length]
  })

  // 주간 데이터 변환
  const weeks: WeeklyBurnBarView[] = data.DATA.map((bar) => {
    const startDate = new Date(bar.SDT * 1000)
    const endDate = new Date(bar.EDT * 1000)

    // 주차 라벨 생성 (예: "Dec W1")
    const month = startDate.toLocaleDateString('en-US', { month: 'short' })
    const weekOfMonth = Math.ceil(startDate.getDate() / 7)
    const label = `${month} W${weekOfMonth}`

    const items: WeeklyBurnItemView[] = bar.ITEMS.map((item) => ({
      name: item.NM,
      // BURN_VAL은 NESO 단위
      burnValue: Number(item.BURN_VAL),
      count: item.CNT,
      color: categoryColors[item.NM] || '#888888',
    }))

    const total = items.reduce((sum, item) => sum + item.burnValue, 0)

    return {
      label,
      startDate,
      endDate,
      items,
      total,
    }
  })

  return {
    weeks,
    categories,
    categoryColors,
  }
}
